import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Button from '../Button';
import Constants from '../../submodules/logictry_config/constants';
import WindowSize from '../../services/WindowSize';
import Messenger from '../../services/Messenger';
import MediaUploadTile from '../MediaUploadTile/index';
import Navigation from '../../services/Navigation';
import UserAccount from '../../services/UserAccount';
import LogicBaseRelationshipCache from '../../services/cache/LogicBaseRelationshipCache';
import LogicBaseBlockCache from '../../services/cache/LogicBaseBlockCache';
import LogicBaseUserReportCache from '../../services/cache/LogicBaseUserReportCache';
import LogicBaseUserCache from '../../services/cache/LogicBaseUserCache';
import { getUserFollowers, getUserFollowees } from '../../services/Pagination';
import Popup from '../Popup';
import MenuList from '../Popup/MenuList';
import MenuItem from '../Popup/MenuItem';
import AppConstants from '../../submodules/logictry_config/s3_config.json';
import ConfirmDialog from '../ConfirmDialog';
import shortenLikesCount from '../../utils/shortenLikesCount';
import { ChatUnfilledIcon, EllipsisIcon } from '../../styledhtml/Icon';
import LogicBaseUsersPopup from '../../components/LogicBaseUsersPopup';
import Tooltip from '../Tooltip';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import ClampedText from '../ClampedText';
import { LOGIC_BASE_CANVAS_FADE } from '../../styledhtml/LogicBaseContentLayout';
import UserApi from '../../services/server/UserApi';

const ProfileSection = styled.div`
  display: flex;
  flex-direction: row;
`;
const ClickToGoToAccount = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  cursor: pointer;
`
const ProfileInfo = styled.div`
  padding-top: 0rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
`;
const UsernameFullname = styled.div`
  display: flex;
  gap: 0.5rem;
  min-width: 0;
  align-items: center;
  ${Constants.MediaMobile} {
    gap: 0.5rem;
  }
  > div:first-child {
    display: flex;
    align-items: center;
    cursor: pointer;
    min-width: 0;
    ${(p) => p.$singleLineUsername && 'flex: 1;'}
    > div:first-child {
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
      ${(p) => p.$singleLineUsername && 'flex: 1;'}
      ${(p) => (p.$singleLineUsername ? `
        display: block;
        white-space: nowrap;
        overflow-wrap: normal;
        word-break: normal;
      ` : `
        -webkit-line-clamp: 3;
        display: box;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        white-space: normal;
        overflow-wrap: break-word;
        word-break: break-word;
      `)}
    }
  }
  img {
    height: 16px;
    margin-left: 0.5rem;
  }
`;
const Stats = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;
const MenuWrapper = styled.div`
  i {
    margin-right: 1rem;
    justify-content: center;
    display: flex;
    width: 1rem;
  }
`;
const MobileSocialCountsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 0 1.5rem;
  margin-top: 0.75rem;
  width: 100%;
  box-sizing: border-box;
  > div {
    cursor: pointer;
    display: inline-flex;
    align-items: baseline;
    flex-wrap: wrap;
    white-space: normal;
    > span {
      margin-right: 0.35rem;
      font-size: 1rem;
      font-weight: 600;
    }
  }
`;
const Bio = styled.div`
  margin: 1rem 0 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  > div:first-child {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 1rem;
    justify-content: flex-start;
    gap: 0 1rem;
    > div {
      cursor: pointer;
      display: flex;
      align-items: center;
      > span {
        margin-right: 0.5rem;
        font-size: 1rem;
        font-weight: 600;
      }
    }
    > div:not(:last-child) {
      margin-right: 2rem;
    }
  }
  > div:nth-child(2) {
    margin-bottom: 0.25rem;
  }
  > div:last-child {
    margin-bottom: 0;
  }
`;
const FlagWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 2rem;
  h1 {
    margin-bottom: 1rem;
  }
`;
const FlagButtonsWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: space-around;
  width: 100%;
`;
const ProfileButton = styled.div`
  background: transparent;
  &:hover {
    background: ${Constants.SurfaceHover};
  }
`;
const BioDescriptionText = styled.span`
  color: ${Constants.MediumTextGray};
`;
const MediumProfile = styled.div`
  white-space: pre-wrap;
  max-width: 40rem;
  color: ${Constants.MediumTextGray};
  -webkit-line-clamp: 2;
  display: box;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  font-size: ${Constants.SemiSmallFontSize};
  ${Constants.MediaMobile} {
    font-size: ${Constants.SmallFontSize};
  }
`;
const WebsiteLink = styled.a`
  margin-top: 0.25rem;
  max-width: 40rem;
  -webkit-line-clamp: 1;
  display: box;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export default class UserProfileHeader extends LogictryComponent {
  static propTypes = {
    user: PropTypes.object,
    small: PropTypes.bool,
    medium: PropTypes.bool,
    large: PropTypes.bool,
    hideFollow: PropTypes.bool,
    hideProfile: PropTypes.bool,
    profileFullWidth: PropTypes.bool,
    singleLineUsername: PropTypes.bool,
    /** When true, profile photo cannot be changed from this header (use e.g. /account/settings). The main app profile passes this. */
    disableProfileAvatarUpload: PropTypes.bool,
    /** When set and the user has stories (`lastStoryTime`), avatar click opens stories instead of navigating. */
    onAvatarStoryClick: PropTypes.func,
    /** When true and the user has stories (`lastStoryTime`), show the gradient ring around the avatar. Prefer true only on profile + curated home surfaces. */
    showStoryAvailabilityRing: PropTypes.bool,
  }
  state = {
    overrideRelationship: false,
    menuOpen: false,
    blockUser: false,
    flagUser: false,
    showFollowers: false,
    showConfirmUnsubscribe: false,
    seeEmailOpen: false,
    seeEmailLoading: false,
    seeEmailValue: null,
    seeEmailError: null,
    seeEmailCopied: false,
  }
  componentDidMount() {
    UserAccount.onStateUpdate(this);
    LogicBaseUserCache.onStateUpdate(this);
    LogicBaseRelationshipCache.onStateUpdate(this);
    LogicBaseBlockCache.onStateUpdate(this);
    LogicBaseUserReportCache.onStateUpdate(this);
    WindowSize.onStateUpdate(this);
  }
  componentWillUnmount() {
    UserAccount.offStateUpdate(this);
    LogicBaseUserCache.offStateUpdate(this);
    LogicBaseRelationshipCache.offStateUpdate(this);
    LogicBaseBlockCache.offStateUpdate(this);
    LogicBaseUserReportCache.offStateUpdate(this);
    WindowSize.offStateUpdate(this);
  }
  goToAccount = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { user } = this.props;
    Navigation.push(Navigation.userProfile(user));
  }
  login = () => {
    UserAccount.showLoginPopup();
  }
  accountSettings = () => {
    Navigation.push(Navigation.accountSettings);
  }
  block = () => {
    const { user } = this.props;
    const youBlocked = LogicBaseBlockCache.query({ br: UserAccount.account._id, be: user._id })
    if (youBlocked && youBlocked[0]) {
      setTimeout(() => {
        LogicBaseBlockCache.__deleteQueryFromCache({ br: UserAccount.account._id, be: user._id });
        LogicBaseBlockCache.delete(youBlocked && youBlocked[0], true);
      });
      this.setState({ blockUser: false });
    } else {
      setTimeout(() => {
        LogicBaseBlockCache.__deleteQueryFromCache({ br: UserAccount.account._id, be: user._id });
        LogicBaseBlockCache.create(LogicBaseBlockCache.createObject({
          br: UserAccount.account._id,
          be: user._id,
        }));
      });
      this.setState({ blockUser: false });
    }
    this.forceUpdate();
  }
  flag = () => {
    const { user } = this.props;
    const youFlagged = LogicBaseUserReportCache.query({ o: UserAccount.account._id, u: user._id })
    if (youFlagged && youFlagged[0]) {
      setTimeout(() => {
        LogicBaseUserReportCache.__deleteQueryFromCache({ o: UserAccount.account._id, u: user._id });
        LogicBaseUserReportCache.delete(youFlagged && youFlagged[0], true);
      });
      this.setState({ flagUser: false });
    } else {
      const d = document.getElementById('userprofileheader__flagdescription').value;
      if (!d) return;
      setTimeout(() => {
        LogicBaseUserReportCache.__deleteQueryFromCache({ o: UserAccount.account._id, u: user._id });
        LogicBaseUserReportCache.create(LogicBaseUserReportCache.createObject({
          o: UserAccount.account._id,
          u: user._id,
          d,
        }));
      });
      this.setState({ flagUser: false });
    }
    this.forceUpdate();
  }
  follow = (privateAccount, confirmedUnsubscribe) => {
    const { isLoggedIn } = UserAccount;
    if (!isLoggedIn) {
      return UserAccount.showLoginPopup();
    }
    const { user } = this.props;
    const relationship = ((isLoggedIn && LogicBaseRelationshipCache.query({ fr: UserAccount.account._id, fe: user._id })) || [])[0];
    const isPendingFollow = relationship?.requested || this.state.overrideRelationship === 'Requested';
    const isEndingFollow = !!(relationship && !isPendingFollow);
    if (isEndingFollow && !confirmedUnsubscribe) {
      return this.setState({ showConfirmUnsubscribe: true });
    }
    const theirSocial = LogicBaseUserCache.get(user._id);
    const yourSocial = LogicBaseUserCache.get(UserAccount.account._id);
    if (relationship) {
      if (!(relationship.requested || this.state.overrideRelationship === 'Requested')) {
        if (theirSocial) theirSocial.followers -= 1;
        if (yourSocial) yourSocial.followees -= 1;
      }
      setTimeout(() => {
        LogicBaseRelationshipCache.delete(relationship, true);
        this.clearCaches();
      });
      const theyFollowYou = (isLoggedIn) && (LogicBaseRelationshipCache.query({ fe: UserAccount.account._id, fr: user._id }) || [])[0];
      this.setState({ overrideRelationship: (theyFollowYou ? 'Follow back' : 'Follow') });
    } else {
      const newRelationship = { fr: UserAccount.account._id, fe: user._id };
      if (privateAccount) {
        newRelationship.r = true;
        this.setState({ overrideRelationship: 'Requested' });
      } else {
        if (theirSocial) theirSocial.followers += 1;
        if (yourSocial) yourSocial.followees += 1;
        this.setState({ overrideRelationship: '✓ Following' });
      }
      setTimeout(() => {
        LogicBaseRelationshipCache.create(LogicBaseRelationshipCache.createObject(newRelationship), true);
        this.clearCaches();
      });
    }
    this.forceUpdate();
  }
  clearCaches = () => {
    // Adding a delay to allow the indices to sync server side
    setTimeout(() => {
      const { user } = this.props;
      LogicBaseRelationshipCache.__deleteQueryFromCache({ fr: UserAccount.account._id, fe: user._id });
      const userFollowers = getUserFollowers(user._id);
      const userFollowees = getUserFollowees(UserAccount.account._id);
      if (userFollowers) userFollowers.initialize();
      if (userFollowees) userFollowees.initialize();
    }, 250);
  }
  verifyAccount = () => {
    const { user } = this.props;
    let logicbaseuser = LogicBaseUserCache.get(user._id);
    if (logicbaseuser._id) {
      if (logicbaseuser.verified) {
        logicbaseuser.verified = false;
        logicbaseuser.logicCircle = false;
      } else {
        logicbaseuser.verified = true;
      }
      LogicBaseUserCache.update(logicbaseuser);
    } else {
      logicbaseuser = LogicBaseUserCache.createObject({ _id: user._id, a: 0, fr: 0, fe: 0, v: true });
      LogicBaseUserCache.create(logicbaseuser);
    }
  }
  toggleLogicCircle = () => {
    const { user } = this.props;
    let logicbaseuser = LogicBaseUserCache.get(user._id);
    if (logicbaseuser._id) {
      if (logicbaseuser.logicCircle) {
        logicbaseuser.logicCircle = false;
      } else {
        logicbaseuser.logicCircle = true;
        if (!logicbaseuser.verified) logicbaseuser.verified = true;
      }
      LogicBaseUserCache.update(logicbaseuser);
    } else {
      logicbaseuser = LogicBaseUserCache.createObject({ _id: user._id, a: 0, fr: 0, fe: 0, v: true, lc: true });
      LogicBaseUserCache.create(logicbaseuser);
    }
  }
  /** Dev-only: fetch email via GET /users?ids= (server allowEmails for isDevAccount). Never render email on the profile surface. */
  seeEmail = async () => {
    const { user } = this.props;
    if (!UserAccount.isDevAccount || !user?._id) return;
    this.setState({
      menuOpen: false,
      seeEmailOpen: true,
      seeEmailLoading: true,
      seeEmailValue: null,
      seeEmailError: null,
      seeEmailCopied: false,
    });
    try {
      const results = await UserApi.getUsers([user._id]);
      const row = Array.isArray(results) ? results.find((u) => u && u._id === user._id) : null;
      const email = row?.email || null;
      this.setState({
        seeEmailLoading: false,
        seeEmailValue: email,
        seeEmailError: email ? null : 'No email on file for this user.',
      });
    } catch {
      this.setState({
        seeEmailLoading: false,
        seeEmailValue: null,
        seeEmailError: 'Could not load email.',
      });
    }
  }
  copySeeEmail = async () => {
    const { seeEmailValue } = this.state;
    if (!seeEmailValue) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(seeEmailValue);
      } else {
        const input = document.createElement('input');
        input.value = seeEmailValue;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      this.setState({ seeEmailCopied: true });
    } catch {
      this.setState({ seeEmailCopied: false, seeEmailError: 'Copy failed.' });
    }
  }
  render() {
    const { mobile, tablet } = WindowSize;
    const compact = tablet;
    const { user, small, medium, large, hideFollow, hideProfile, profileFullWidth, singleLineUsername, disableProfileAvatarUpload, onAvatarStoryClick, showStoryAvailabilityRing } = this.props;
    const {
      overrideRelationship,
      menuOpen,
      blockUser,
      flagUser,
      showFollowers,
      showConfirmUnsubscribe,
      seeEmailOpen,
      seeEmailLoading,
      seeEmailValue,
      seeEmailError,
      seeEmailCopied,
    } = this.state;
    const { isLoggedIn, isDevAccount } = UserAccount;
    const isValidUser = user && user._id;
    const editProfileButtonStyle = {
      cursor: 'pointer',
      border: `1px solid ${Constants.LightBorderColor}`,
      color: Constants.DarkTextColor,
      backgroundColor: 'transparent',
      padding: '0.25rem 0.75rem',
      borderRadius: '0.5rem',
      fontSize: mobile && Constants.SmallFontSize || Constants.SemiSmallFontSize,
    };
    const signingUrl = `/v1${AppConstants.S3.signingUrls.userprofiles}`;
    const s3Path = isValidUser && `${user._id}/`;
    const isUserAccount = isValidUser && isLoggedIn && UserAccount.account._id === user._id;
    const social = isValidUser && LogicBaseUserCache.get(user._id);
    const { followers, followees, verified, socialSettings, logicCircle, lastStoryTime } = social || {};
    const hasStories = !!lastStoryTime;
    const displayStoryRing = hasStories && !!showStoryAvailabilityRing;
    const relationshipFound = (isValidUser && isLoggedIn && !hideFollow) ? (LogicBaseRelationshipCache.query({ fr: UserAccount.account._id, fe: user._id }) || this.lastRelationshipFound) : [];
    const theyFollowYou = (isValidUser && isLoggedIn && !hideFollow) && (LogicBaseRelationshipCache.query({ fe: UserAccount.account._id, fr: user._id }) || [])[0];
    this.lastRelationshipFound = relationshipFound;
    const youFlagged = (isValidUser && isLoggedIn && !hideFollow) ? (LogicBaseUserReportCache.query({ o: UserAccount.account._id, u: user._id }) || this.youFlagged) : [];
    this.youFlagged =  youFlagged;
    const youBlocked = (isValidUser && isLoggedIn && !hideFollow) ? (LogicBaseBlockCache.query({ br: UserAccount.account._id, be: user._id }) || this.youBlocked) : [];
    const theyBlocked = (isValidUser && isLoggedIn && !hideFollow) ? (LogicBaseBlockCache.query({ br: user._id, be: UserAccount.account._id }) || this.theyBlocked) : [];
    this.youBlocked = youBlocked;
    this.theyBlocked = theyBlocked;
    const relationship = (relationshipFound || [])[0];
    const imageSize = (small && '2rem') || (medium && (hideProfile && hideFollow && (mobile && '3.5rem' || '4rem') || (mobile && '4rem' || '5rem'))) || (large && (mobile ? '5rem' : tablet ? '7rem' : Constants.BoxSize));
    const disableEditing = !isValidUser || !isUserAccount || !large;
    const avatarDisableEditing = disableEditing || !!disableProfileAvatarUpload;
    const showAvatarStoryOverlay = typeof onAvatarStoryClick === 'function' && hasStories;
    const showAvatarNavOverlay = avatarDisableEditing && !showAvatarStoryOverlay && !isUserAccount;
    const privateAccount = socialSettings && socialSettings.s === 'private';
    const backgroundImage = UserAccount.formatUserImage(user) || Constants.Avatar;
    const username = isValidUser && (user.fullname || `@${user.username || `${user._id.slice(0, 8)}...`}`);
    const useSingleLineUsername = singleLineUsername || small;
    const followButtonStyle = { ...editProfileButtonStyle };
    const highlightGreen = isUserAccount && !backgroundImage;
    if (!relationshipFound) {
      followButtonStyle.backgroundColor = Constants.isDarkMode
        ? 'rgba(255,255,255,0.12)'
        : Constants.White;
    }
    let initials;
    if (isValidUser) initials = (user.fullname && user.fullname.split(' ').map((_string) => _string.charAt(0)).slice(0, 3)) || (user.username && user.username.slice(0, 3));
    const storyRingGradient = 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)';
    const border = displayStoryRing
      ? `3px solid transparent`
      : highlightGreen
        ? `2px solid ${Constants.PrimaryColor}`
        : Constants.LightBorder;
    const followButtonText = (overrideRelationship || ((relationshipFound && (relationship ? (relationship.requested ? 'Requested' : '✓ Following') : (theyFollowYou ? 'Follow back' : 'Follow')))));
    // Follower/following counts are public aggregates for all viewers (product decision 2026-07-14).
    const showFollowCounts = !!(isValidUser && large);
    const followerCountsNodes = showFollowCounts && (
      <>
        <div onClick={() => this.setState({ showFollowers: 'Followers' })}><span>{shortenLikesCount(followers || 0)}</span>{' '}{followers === 1 ? 'follower' : 'followers'}</div>
        <div onClick={() => this.setState({ showFollowers: 'Following' })}><span>{shortenLikesCount(followees || 0)}</span> following</div>
      </>
    );
    const followButton = !hideFollow && isValidUser && !theyBlocked?.[0] && (isUserAccount ? (
      <Stats>
        {<ProfileButton style={editProfileButtonStyle} onClick={this.accountSettings}>Edit Profile</ProfileButton>}
      </Stats>
    ) : (
      <Stats>
        {((youBlocked && youBlocked[0]) && <ProfileButton style={followButtonStyle} onClick={() => this.block()}>Unblock</ProfileButton>)
          || followButtonText && <ProfileButton style={followButtonStyle} onClick={() => this.follow(privateAccount)}>{followButtonText}</ProfileButton>}
      </Stats>
    ));
    const messageButton = !hideFollow && isValidUser && !theyBlocked?.[0] && !youBlocked?.[0] && !isUserAccount && large && followButtonText && (!privateAccount || followButtonText === '✓ Following') && <Stats>
      <ProfileButton style={{ ...followButtonStyle, backgroundColor: Constants.PrimaryColor, color: 'white', borderColor: Constants.PrimaryColor, display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={() => {
        const { isLoggedIn } = UserAccount;
        if (!isLoggedIn) {
          return UserAccount.showLoginPopup();
        }
        return Messenger.updateView('messenger', user._id)
      }}><ChatUnfilledIcon style={{ width: 14, height: 14, position: 'relative', filter: 'invert(1)' }} />Message</ProfileButton>
    </Stats>;
    const moreIcon = !hideFollow && !small && isLoggedIn && !isUserAccount && !theyBlocked?.[0] && !youBlocked?.[0] && followButtonText && <EllipsisIcon
      style={{ width: 14, height: 14, marginLeft: 8 }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.anchorEl = e.target; this.setState({ menuOpen: true }); }}
    />;

    const usernameHandle = (user.username && <div style={{ fontWeight: 500 }} onClick={this.goToAccount}>{`@${user.username}`}</div>)
      || (isUserAccount && <ProfileButton style={editProfileButtonStyle} onClick={this.accountSettings}>Edit Username</ProfileButton>)
      || <div style={{ fontWeight: 500 }} onClick={this.goToAccount}>{`@${user._id.slice(0, 8)}...`}</div>;
    const description = <div>
      <div style={{ display: 'flex' }}>{usernameHandle}</div>
      {user.description
        ? <ClampedText maxWidth="40rem" fadeBackground={LOGIC_BASE_CANVAS_FADE}>
          <BioDescriptionText>{user.description}</BioDescriptionText>
        </ClampedText>
        : (isUserAccount && <ProfileButton
          style={{ ...editProfileButtonStyle, marginTop: '0.5rem' }}
          onClick={this.accountSettings}
        >Edit Bio</ProfileButton>)}
      {user.website && <WebsiteLink rel="noopener noreferrer" target="_blank" href={user.website.startsWith('http') ? user.website : `https://${user.website}`}>{user.website}</WebsiteLink>}
    </div>;
    return (
      <>
        <ProfileSection
          style={{ width: small && !hideProfile && '100%', opacity: !theyBlocked ? 0 : null }}
        >
          <div style={{ display: 'flex', alignItems: large ? 'flex-start' : 'center' }}>
            <div style={{
              position: 'relative',
              borderRadius: '50%',
              ...(displayStoryRing ? { background: storyRingGradient, padding: '3px' } : {}),
            }}>
              <div style={{ borderRadius: '50%', overflow: displayStoryRing ? 'hidden' : undefined, background: displayStoryRing ? 'white' : undefined, padding: displayStoryRing ? '2px' : undefined, position: 'relative' }}>
              <div style={{ border: displayStoryRing ? 'none' : border, position: 'absolute', inset: 0, borderRadius: '50%' }}></div>
              {((isUserAccount && !disableEditing) || backgroundImage) ? (
                <MediaUploadTile
                  onlyAllowImage
                  uploadChoiceMenu
                  suppressReadonlyAssetPreview
                  disableEditing={avatarDisableEditing}
                  s3Path={s3Path}
                  signingUrl={signingUrl}
                  style={{
                    width: imageSize,
                    height: imageSize,
                    borderRadius: '50%',
                    backgroundSize: 'cover'
                  }}
                  backgroundImage={backgroundImage}
                  onUploadFinish={(file) => {
                    if (!isUserAccount) return;
                    const imageUrl = file.signedUrl.split('?')[0];
                    UserAccount.changeUserImage(imageUrl);
                  }}
                />
              ) : (<div style={{
                fontSize: (small && Constants.SmallFontSize) || Constants.SemiLargeFontSize,
                width: imageSize,
                height: imageSize,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>{initials}</div>)}
              {showAvatarStoryOverlay && (
                <ClickToGoToAccount
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAvatarStoryClick();
                  }}
                  aria-label="View stories"
                  role="button"
                />
              )}
              {showAvatarNavOverlay && <ClickToGoToAccount onClick={this.goToAccount} />}
              </div>
            </div>
          </div>
          {!hideProfile && <ProfileInfo style={{ padding: (small && '0 0 0 0.5rem') || null, width: small && '100%', paddingLeft: large && (mobile && '1rem' || '2rem') || medium && (mobile && '0.75rem' || '1rem') || small && '0.5rem' }}>
            <UsernameFullname $singleLineUsername={useSingleLineUsername} style={{ width: small && '100%' }}>
              <div onClick={this.goToAccount} style={{ flex: profileFullWidth ? 1 : undefined, minWidth: 0 }}>
                <div style={{
                  fontSize: (small && Constants.SmallFontSize) || (medium && (mobile && Constants.SemiSmallFontSize || Constants.NormalFontSize)) || (large && (mobile && Constants.NormalFontSize || Constants.SemiLargeFontSize)),
                  textAlign: 'left',
                  fontWeight: 500,
                }}>{username}</div>
                {!small && verified && <div style={{ flexShrink: 0 }}>
                  <Tooltip text={`Verified User`}>
                    <img style={small && { marginTop: 5, marginLeft: 7, height: 12 } || (large && mobile && { marginTop: 4, marginLeft: 8, height: 14 }) || { marginTop: 5, marginLeft: 10, height: 16 }} alt="Verified User" src={Constants.VerifiedBadge} />
                  </Tooltip>
                </div>}
                {!small && logicCircle && <div style={{ flexShrink: 0 }}>
                  <Tooltip text={`Verified Creator`}>
                    <img style={small && { marginTop: 5, marginLeft: 4, height: 18 } || (large && mobile && { marginTop: 3, marginLeft: 6, height: 20 }) || { marginTop: 5, marginLeft: 8, height: 26 }} alt="Verified Creator" src={Constants.LogicCircle} />
                  </Tooltip>
                </div>}
              </div>
              {(!compact || !large) && <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, marginLeft: '0.5rem' }}>
                <div>{followButton}</div>
                {messageButton && <div style={{ marginLeft: '0.5rem' }}>{messageButton}</div>}
                {moreIcon}
              </div>}
              {!!menuOpen && <Popup
                onClose={() => this.setState({ menuOpen: false })}
                allowBackgroundClose
                anchorEl={this.anchorEl}
              >
                <MenuWrapper>
                  <MenuList>
                    {isDevAccount && <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.toggleLogicCircle(); this.setState({ menuOpen: false }); }}>
                      <div>{logicCircle ? 'Unverify Creator' : 'Verify Creator'}</div>
                    </MenuItem>}
                    {isDevAccount && <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.verifyAccount(); this.setState({ menuOpen: false }); }}>
                      <div>{verified ? 'Unverify User' : 'Verify User'}</div>
                    </MenuItem>}
                    {isDevAccount && large && <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.seeEmail(); }}>
                      <div>See Email</div>
                    </MenuItem>}
                    <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.setState({ menuOpen: false, blockUser: true }); }}>
                      <div>{((youBlocked && youBlocked[0]) && 'Unblock User') || 'Block User'}</div>
                    </MenuItem>
                    <MenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); this.setState({ menuOpen: false, flagUser: true }); }}>
                      <div>{((youFlagged && youFlagged[0]) && 'Unflag User') || 'Flag User'}</div>
                    </MenuItem>
                  </MenuList>
                </MenuWrapper>
              </Popup>}
            </UsernameFullname>
            {compact && large && !hideFollow && (followButton || messageButton || moreIcon) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                {followButton}
                {messageButton}
                {moreIcon}
              </div>
            )}
            {large && !(compact && showFollowCounts) && (
              <Bio>
                {!(showFollowCounts && compact) && (
                  <div style={!isUserAccount ? { margin: 0 } : null}>
                    {showFollowCounts && followerCountsNodes}
                  </div>
                )}
                {!compact && description}
              </Bio>
            )}
            {medium && <MediumProfile>{user.description}</MediumProfile>}
            {!hideFollow && (youFlagged && youFlagged[0]) && <div>
              <div style={{ background: 'red', color: 'white', padding: '0.25rem 0.5rem', display: 'inline-block' }}>Flagged for review</div>
            </div>}
          </ProfileInfo>}
        </ProfileSection>
        {!hideProfile && compact && showFollowCounts && (
          <MobileSocialCountsRow>{followerCountsNodes}</MobileSocialCountsRow>
        )}
        {!hideProfile && large && compact && <div style={{ marginTop: '1rem' }}>{description}</div>}
        {blockUser && (((youBlocked && youBlocked[0]) && <ConfirmDialog
          text="Confirm Unblock"
          description="Are you sure you wish to unblock the user? They will now be able to follow you."
          open onNo={() => this.setState({ blockUser: false })} onYes={() => this.block()} />)
          || <ConfirmDialog
          text="Confirm Block"
          description="Are you sure you wish to block the user? This will unfollow you both from each other and prevent them from following you."
          open onNo={() => this.setState({ blockUser: false })} onYes={() => this.block()} />
        )}
        {showConfirmUnsubscribe && <ConfirmDialog
          text="Confirm Unsubscribe"
          description="Are you sure you want to unsubscribe? You will unfollow this user."
          open
          onNo={() => this.setState({ showConfirmUnsubscribe: false })}
          onYes={() => {
            this.setState({ showConfirmUnsubscribe: false });
            this.follow(privateAccount, true);
          }}
        />}
        {flagUser && (((youFlagged && youFlagged[0]) && <ConfirmDialog
          text="Confirm Unflag"
          description="Are you sure you wish to unflag the user? We will no longer review the user's profile."
          open onNo={() => this.setState({ flagUser: false })} onYes={() => this.flag()} />)
          ||         <Popup
          allowBackgroundClose
          onClose={() => this.setState({ flagUser: false })}
        >
          <FlagWrapper>
            <h2>Are you sure you wish to flag the user?</h2>
            <p>{`We will review the user's profile and take any neccessary action.`}</p>
            <p>REQUIRED: Please share what is wrong with the profile.</p>
            <textarea
              id="userprofileheader__flagdescription"
              maxLength={4000}
              style={{ width: '100%', border: `1px solid ${Constants.LightTextGray}`, minHeight: 200, padding: 12 }}
            />
            <FlagButtonsWrapper>
              <Button onClick={() => this.setState({ flagUser: false })} color="primary" autoFocus>
                Cancel
              </Button>
              <Button onClick={() => this.flag()} color="primary" autoFocus>
                Save
              </Button>
            </FlagButtonsWrapper>
          </FlagWrapper>
        </Popup>
        )}
        {showFollowers && <LogicBaseUsersPopup
          content={showFollowers}
          id={user._id}
          onClose={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } this.setState({ showFollowers: false })} }
        />}
        {seeEmailOpen && (
          <ConfirmDialog
            open
            allowBackgroundClose
            text="User email"
            description={
              seeEmailLoading
                ? 'Loading…'
                : (seeEmailValue || seeEmailError || 'No email on file for this user.')
            }
            onOk={() => this.setState({
              seeEmailOpen: false,
              seeEmailLoading: false,
              seeEmailValue: null,
              seeEmailError: null,
              seeEmailCopied: false,
            })}
            secondaryTextButton={seeEmailValue ? (seeEmailCopied ? 'Copied' : 'Copy email') : null}
            onSecondaryTextButton={seeEmailValue ? this.copySeeEmail : undefined}
          />
        )}
      </>
    );
  }
}
